import { useState } from "react";
import { useNavigate, useOutletContext } from "fusion-plugin-react-router";
import { LuArrowRight, LuFileSearch, LuShieldCheck } from "react-icons/lu";
import { deformatDateT } from "../../../../shared/utils/formatters";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import {
  cloneQuote,
  fetchPanelData,
  fetchRiskData,
} from "../../../../features/quote/services/quoteService";
import QuoteStep from "../../../quote/enums/QuoteStep";
import { fetchArchiveList } from "../../../buy/services/buyService";
import { TAXI_QUOTE_BRANDREF } from "../../../../config/config";
import PolicyCard from "../../components/dashboard/PolicyCard";
import PolicyDateTimeModal from "../../components/dashboard/PolicyDateTimeModal";
import QuoteFormWrapper from "../../../../shared/components/quoteFormWrapper";
import QuoteStepLoader from "../../../../shared/components/QuoteStepLoader";
import QuoteStepHeading from "../../../../shared/components/QuoteStepHeading";
import Button from "../../../../shared/components/Button";
import PageContentStack from "../../../../shared/components/PageContentStack";

const HomeActionCard = ({ title, description, buttonLabel, onClick, icon }) => (
  <QuoteFormWrapper>
    <div className="tw-flex tw-flex-col tw-items-start tw-gap-4">
      <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-dis-sky-tint tw-text-dis-deep-blue">
        {icon}
      </div>
      <div>
        <h2 className="tw-m-0 tw-text-xl tw-font-bold tw-text-dis-navy">
          {title}
        </h2>
        <p className="tw-mb-0 tw-mt-2 tw-text-base tw-leading-[1.5] tw-text-dis-text-muted">
          {description}
        </p>
      </div>
      <Button
        variant="main"
        fullWidth
        trailingIcon={
          <LuArrowRight className="tw-h-5 tw-w-5" aria-hidden="true" />
        }
        onClick={onClick}
      >
        {buttonLabel}
      </Button>
    </div>
  </QuoteFormWrapper>
);

const HomePage = () => {
  const { indicator, foreName, error } = useOutletContext();
  const STATE = useAppStateStore();
  const navigate = useNavigate();
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [riskLoading, setRiskLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const customerPolicies = STATE.customerPolicies;
  const customerQuotes = STATE.customerQuotes;

  const fetchPanel = async () => {
    try {
      await fetchPanelData(
        navigate,
        null,
        customerQuotes.riskObjectId,
        customerQuotes.startDate,
      );
      await fetchRiskData(customerQuotes.riskObjectId, QuoteStep.TQ_PANEL);
    } catch (error) {
      console.error("Error fetching panel data:", error);
    }
  };

  const activeWithin30Days = async () => {
    STATE.setCurrentStep(QuoteStep.TQ_PANEL_LOADER);
    navigate(`/taxi/quote/start?code=${sessionStorage.getItem("uberCode")}`);
    await fetchPanel();
  };

  const inactiveWithin31To60Days = async () => {
    setRiskLoading(true);
    await fetchRiskData(customerQuotes.riskObjectId, QuoteStep.TQ1);
    setRiskLoading(false);
    navigate(`/taxi/quote/start?code=${sessionStorage.getItem("uberCode")}`);
  };

  const inactive60DaysPlus = async () => {
    setRiskLoading(true);
    await cloneQuote(customerQuotes.riskObjectId, QuoteStep.TQ1);
    setRiskLoading(false);
    navigate(`/taxi/quote/start?code=${sessionStorage.getItem("uberCode")}`);
  };

  const handleQuoteNavigation = async () => {
    if (customerQuotes.quoteStatus === "ActiveWithin30Days") {
      await activeWithin30Days();
    } else if (customerQuotes.quoteStatus === "InactiveWithin31To60Days") {
      await inactiveWithin31To60Days();
    } else if (customerQuotes.quoteStatus === "Inactive60DaysPlus") {
      await inactive60DaysPlus();
    }
  };

  const quoteNavigationParent = () => {
    const isStartDateInPast = new Date(customerQuotes.startDate) < new Date();
    if (
      isStartDateInPast &&
      customerQuotes.quoteStatus === "ActiveWithin30Days"
    ) {
      setIsPolicyModalOpen(true);
    } else {
      handleQuoteNavigation();
    }
  };

  const handlePolicyDocuments = async (policy) => {
    const policyToUse = policy || customerPolicies?.customerPolicies?.[0];
    if (!policyToUse) return;

    const requestBody = {
      riskObjectId: policyToUse.riskObjectId,
      riskObjectHistoryId: policyToUse.riskObjectHistoryId,
      BrandRef: TAXI_QUOTE_BRANDREF,
      documentTagStage: "All",
      documentTagCategory: "PortalDocuments",
      LatestVaultArchivedItemsOnly: true,
    };

    setDocumentsLoading(true);
    try {
      const response = await fetchArchiveList(requestBody);
      STATE.setDocuments(response);
      navigate("/documents");
    } catch (error) {
      console.error("Failed to fetch the archive vault list:", error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  if (documentsLoading || riskLoading) {
    return (
      <QuoteStepLoader
        title="Driver Insurance Services"
        description={
          documentsLoading
            ? "Preparing your policy documents."
            : "Getting your quote ready."
        }
        message={
          documentsLoading
            ? "We're fetching your policy documents."
            : "We're retrieving your quote details."
        }
      />
    );
  }

  let content;
  if (indicator === "Policy" && customerPolicies?.customerPolicies?.length) {
    content = customerPolicies.customerPolicies.map((policy) => (
      <QuoteFormWrapper key={policy?.policyNumber}>
        <PolicyCard
          Logo={policy?.schemeTableId}
          navigate={navigate}
          policyStatus={policy?.policyStatus}
          carName={policy?.vehicleMake}
          carReg={policy?.registrationNumber}
          policyNumber={policy?.policyNumber}
          expiryDate={deformatDateT(policy?.expiryDate)}
          onClick={() => handlePolicyDocuments(policy)}
        />
      </QuoteFormWrapper>
    ));
  } else if (indicator === "Quote") {
    content = (
      <HomeActionCard
        title="View my quotes"
        description="View your saved quote and continue to your available insurance options."
        buttonLabel="View my quotes"
        onClick={quoteNavigationParent}
        icon={<LuFileSearch className="tw-h-6 tw-w-6" aria-hidden="true" />}
      />
    );
  } else if (indicator === "Policy") {
    content = (
      <HomeActionCard
        title="No active policies"
        description="You currently have no active policies. You can start a new quote whenever you're ready."
        buttonLabel="Get a new quote"
        onClick={() => navigate("/taxi/quote/start")}
        icon={<LuShieldCheck className="tw-h-6 tw-w-6" aria-hidden="true" />}
      />
    );
  } else {
    content = (
      <HomeActionCard
        title="Ready to start your quote?"
        description="Get a quote for your taxi insurance and explore the cover available to you."
        buttonLabel="Get a new quote"
        onClick={() => navigate("/taxi/quote/start")}
        icon={<LuShieldCheck className="tw-h-6 tw-w-6" aria-hidden="true" />}
      />
    );
  }

  return (
    <>
      <PageContentStack>
        <div>
          <p className="tw-m-0 tw-mb-1 tw-text-base tw-text-dis-text-muted">
            Hi, {foreName}
          </p>
          <QuoteStepHeading
            title="Driver Insurance Services"
            className="tw-m-0"
            titleClassName="tw-m-0"
          />
        </div>
        {content}
        {error && (
          <div className="tw-rounded-dis-card tw-border tw-border-solid tw-border-dis-error tw-bg-dis-error-bg tw-p-4 tw-text-base tw-text-dis-error">
            {error}
          </div>
        )}
      </PageContentStack>
      {isPolicyModalOpen && (
        <PolicyDateTimeModal
          onClose={() => setIsPolicyModalOpen(false)}
          riskObjectId={customerQuotes.riskObjectId}
          actions={handleQuoteNavigation}
        />
      )}
    </>
  );
};

export default HomePage;
